import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  ActivityIndicator,
  TouchableOpacity,
  ScrollView,
  ViewStyle,
  View,
  FlatList,
  TextStyle,
} from 'react-native'
import {
  color,
  Header,
  Button,
  iconArrowBack,
  SectionHeader,
  Text,
  HistoryRow,
  QuizHeader,
  iconTrophySmall,
  SvgIcon,
  iconPath,
} from '@trivie/ui-mobile'
import { useStores, remove, GetLimitStatus } from '@trivie/core'
import { DateTime } from 'luxon'
import { useHistory, useLocation } from 'react-router-dom'
import { Instance } from 'mobx-state-tree'
import { PreQuiz, QuizResource, QuizResult } from '@trivie/core/src/models/UserQuiz'
import { ResourceRow } from '@trivie/ui-web/components/resource-row'
// @ts-ignore
import { ReactComponent as Eye } from './eye.svg'
// @ts-ignore
import { ReactComponent as QuizNull } from './quiz-null.svg'
import { QuizStatsPanel } from './quiz-stats-panel'
import { CountdownTimer } from '@trivie/ui-mobile/components/countdown-timer'
import { Box, useMediaQuery } from '@mui/material'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'

const useQuery = () => {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

export const QuizDetailScreen: React.FunctionComponent = observer(() => {
  const { contestStore, gameStore, resourceStore, libraryStore, quizStore, userStore } = useStores()
  const { setQuiz, setReview } = gameStore
  const { getQuizDetails, quizDetails, isLoading, limitStatus, getLimitStatus } = quizStore
  const { selectedTopic } = libraryStore
  const { user, enterpriseTier } = userStore
  const { getContestActive, contestActive } = contestStore

  const query = useQuery()

  const params = {
    topic_id: Number(query.get('topic_id')) || null,
    quiz_id: Number(query.get('quiz_id')) || null,
    assignment_id: Number(query.get('assignment_id')) || null,
    subscription_user_id: Number(query.get('subscription_user_id')) || null,
    path_id: Number(query.get('path_id')) || null,
    content_user_id: Number(query.get('content_user_id')) || null,
  }

  const h = useHistory()

  const [viewedReq, setViewedReq] = useState(false)

  const onFocus = () => {
    const req = localStorage.getItem('viewed_req')
    if (req) {
      const requisite = JSON.parse(req)
      resourceStore.setEndViewed(DateTime.now().toUTC().toISO())
      resourceStore.markAsViewed(requisite.resource_id, requisite.metadata)
      resourceStore.setRequisite(null)
      localStorage.removeItem('viewed_req')
      setViewedReq(true)
    } else {
      const resourceData = localStorage.getItem('viewing-resource')
      if (resourceData) {
        const resource = JSON.parse(resourceData)
        if (resourceData) {
          localStorage.removeItem('viewing-resource')
          resourceStore.setEndViewed(DateTime.now().toUTC().toISO())
          resourceStore.markAsViewed(resource.resource_id, resource.metadata)
        }
      }
    }
  }

  useEffect(() => {
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  useEffect(() => {
    onFocus()
    if (user && params.quiz_id !== 0) {
      if (enterpriseTier) {
        getContestActive(params.quiz_id!, 'quiz')
      }

      getQuizDetails(user.id, params).then((data: any) => {
        if (data.kind === 'not-found') {
          h.goBack()
        } else {
          const req = data?.resources?.filter((resource: any) => resource.is_required)[0]
          if (req && req.last_viewed) {
            if (
              DateTime.fromISO(req.last_viewed).diffNow('milliseconds').milliseconds * -1 <
              900000
            ) {
              setViewedReq(true)
            } else {
              setViewedReq(false)
            }
          }
        }
      })
      getLimitStatus()
    }
  }, [])

  const start = () => {
    setQuiz(undefined)
    if (params.subscription_user_id) {
      h.replace(
        `/game/quiz/?subscription_user_id=${params.subscription_user_id}&topic_id=${params.topic_id}&quiz_id=${params.quiz_id}&path_id=${params.path_id}&content_user_id=${params.content_user_id}`,
      )
    } else {
      h.replace(
        `/game/quiz/?assignment_id=${params.assignment_id}&topic_id=${params.topic_id}&quiz_id=${params.quiz_id}&path_id=${params.path_id}&content_user_id=${params.content_user_id}`,
      )
    }
  }

  const [viewingTextSource, setViewingTextSource] = useState<any>()

  const viewResource = (resource: any) => {
    resourceStore.getSourceDetails(resource.source_id).then((data) => {
      if (data) {
        if (resource.is_required) {
          localStorage.setItem(
            'viewed_req',
            JSON.stringify({ ...data, metadata: resource.metadata }),
          )
        }
        resourceStore.setStartViewed(DateTime.now().toUTC().toISO())
        if (data?.raw_text) {
          setViewingTextSource({ ...data, metadata: resource.metadata })
        } else if (data?.isVideo || data?.isAudio) {
          localStorage.setItem(
            'viewing-resource',
            JSON.stringify({ resource_id: data.resource_id, metadata: resource.metadata }),
          )
          h.push(
            `/library/source/?source_id=${resource.source_id}&assignment_id=${resource.metadata.assignment_id}&content_user_id=${resource.metadata.content_user_id}&path_id=${resource.metadata.path_id}&quiz_id=${resource.metadata.quiz_id}&topic_id=${resource.metadata.topic_id}`,
          )
        } else if (data?.filename_or_url) {
          localStorage.setItem(
            'viewing-resource',
            JSON.stringify({ resource_id: data.resource_id, metadata: resource.metadata }),
          )
          window.open(
            // @ts-ignore
            data.converted_pdf_url ? data.converted_pdf_url : data.filename_or_url,
            '_blank',
            'noopener',
          )
        }
      }
    })
  }

  const goToResults = (quiz: any) => {
    quiz.topic_id
      ? h.push(`/results/${quiz.result_id}/${quiz.topic_id}`, {
          from: 'quiz-detail',
          quiz,
        })
      : h.push(`/results/${quiz.result_id}`, {
          from: 'quiz-detail',
          quiz,
        })
  }

  const confirmRequisite = () => {
    if (quizDetails?.resources?.filter((resource) => resource.is_required)[0]?.source_id) {
      resourceStore
        .getSourceDetails(
          quizDetails?.resources?.filter((resource) => resource.is_required)[0]?.source_id,
        )
        .then((data) => {
          if (data) {
            setViewedReq(true)
            localStorage.setItem(
              'viewed_req',
              JSON.stringify(quizDetails?.resources?.filter((resource) => resource.is_required)[0]),
            )
            if (data?.raw_text) {
              setViewingTextSource({
                ...data,
                metadata: quizDetails?.resources?.filter((resource) => resource.is_required)[0]
                  ?.metadata,
              })
            } else {
              window.open(
                // @ts-ignore
                data.converted_pdf_url ? data.converted_pdf_url : data.filename_or_url,
                '_blank',
                'noopener',
              )
            }
          }
          resourceStore.setStartViewed(DateTime.now().toUTC().toISO())
        })
    }
  }

  const goToResult = (quiz: Instance<typeof QuizResult>) => {
    if (user) {
      setReview(undefined)
      goToResults({ result_id: quiz.result_id, topic_id: params.topic_id })
    }
  }

  const renderHistory = () => {
    return quizDetails?.history && quizDetails?.history?.length > 0 ? (
      <View style={{ paddingTop: 8 }}>
        <SectionHeader headerTx="Quiz.history" />
        {quizDetails?.history.map((quiz: Instance<typeof QuizResult>) => (
          <HistoryRow
            key={quiz.result_id || quiz.date_taken}
            title={quizDetails?.title}
            date={quiz.date_taken}
            correct={quiz.number_correct}
            total={quiz.number_of_questions}
            medal={quiz.medal_level}
            onPress={() => goToResult(quiz)}
            enterpriseTier={enterpriseTier}
          />
        ))}
      </View>
    ) : null
  }

  const renderRequiredResource = () => {
    const required = quizDetails?.resources.find((resource) => Boolean(resource?.is_required))
    if (required) {
      return (
        <ResourceRow
          onPress={() => viewResource(required)}
          resource={required}
          style={{ marginBottom: 16 }}
        />
      )
    }
  }

  const [showTooltip, setShowTooltip] = useState(false)

  const formattedDate = quizDetails?.end_date
    ? DateTime.fromISO(quizDetails?.end_date).toFormat('MM/dd/yyyy')
    : ''

  let statCards = [
    {
      title: String(quizDetails?.number_of_questions),
      subtitle: 'Quiz.questionsLabel',
      icon: (
        <View
          style={{
            flex: 0.2,
            marginRight: 8,
            flexDirection: 'row',
            justifyContent: 'flex-start',
          }}
        >
          <svg width="20" height="26" viewBox="0 0 20 26" fill="none">
            <path
              d="M10 0C9.38668 0 8.76001 0.0533335 8.13335 0.186667C4.45335 0.893333 1.52001 3.86667 0.826681 7.54667C0.186681 11.0267 1.46668 14.2267 3.78668 16.2933C4.36001 16.8 4.66668 17.5067 4.66668 18.2533V21.3333C4.66668 22.8 5.86668 24 7.33335 24H7.70668C8.17335 24.8 9.01335 25.3333 10 25.3333C10.9867 25.3333 11.84 24.8 12.2933 24H12.6667C14.1333 24 15.3333 22.8 15.3333 21.3333V18.2533C15.3333 17.52 15.6267 16.8 16.1867 16.3067C18.12 14.6 19.3333 12.1067 19.3333 9.33333C19.3333 4.17333 15.16 0 10 0ZM10.6667 14.6667H9.33335V11.2133L6.89335 8.78667L7.84001 7.84L10 10L12.16 7.84L13.1067 8.78667L10.6667 11.2267V14.6667ZM12 21.3333C11.9867 21.3333 11.9733 21.32 11.96 21.32V21.3333H8.04001V21.32C8.02668 21.32 8.01335 21.3333 8.00001 21.3333C7.62668 21.3333 7.33335 21.04 7.33335 20.6667C7.33335 20.2933 7.62668 20 8.00001 20C8.01335 20 8.02668 20.0133 8.04001 20.0133V20H11.96V20.0133C11.9733 20.0133 11.9867 20 12 20C12.3733 20 12.6667 20.2933 12.6667 20.6667C12.6667 21.04 12.3733 21.3333 12 21.3333ZM12 18.6667H8.00001C7.62668 18.6667 7.33335 18.3733 7.33335 18C7.33335 17.6267 7.62668 17.3333 8.00001 17.3333H12C12.3733 17.3333 12.6667 17.6267 12.6667 18C12.6667 18.3733 12.3733 18.6667 12 18.6667Z"
              fill="#FFCE51"
            />
          </svg>
        </View>
      ),
      options: {},
      showTooltip: false,
    },
  ] as any

  if (formattedDate) {
    statCards.push({
      title: formattedDate,
      subtitle: 'Quiz.availableUntilLabel',
      icon: (
        <View
          style={{
            flex: 0.2,
            marginRight: 8,
            flexDirection: 'row',
            justifyContent: 'flex-start',
          }}
        >
          <svg
            width="24"
            height="27"
            viewBox="0 0 24 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21.3333 3H20V1.66667C20 0.933332 19.4 0.333332 18.6667 0.333332C17.9333 0.333332 17.3333 0.933332 17.3333 1.66667V3H6.66667V1.66667C6.66667 0.933332 6.06667 0.333332 5.33333 0.333332C4.6 0.333332 4 0.933332 4 1.66667V3H2.66667C1.18667 3 0.0133333 4.2 0.0133333 5.66667L0 24.3333C0 25.8 1.18667 27 2.66667 27H21.3333C22.8 27 24 25.8 24 24.3333V5.66667C24 4.2 22.8 3 21.3333 3ZM20 24.3333H4C3.26667 24.3333 2.66667 23.7333 2.66667 23V9.66667H21.3333V23C21.3333 23.7333 20.7333 24.3333 20 24.3333ZM6.66667 12.3333H10.6667C11.4 12.3333 12 12.9333 12 13.6667V17.6667C12 18.4 11.4 19 10.6667 19H6.66667C5.93333 19 5.33333 18.4 5.33333 17.6667V13.6667C5.33333 12.9333 5.93333 12.3333 6.66667 12.3333Z"
              fill="#551BFA"
            />
          </svg>
        </View>
      ),
      options: {},
      showTooltip: false,
    })
  }

  if (quizDetails?.completed_attempts && quizDetails?.completed_attempts > 0) {
    statCards.push({
      title: `${Math.ceil(
        Number(quizDetails?.best_score) * 0.01 * quizDetails?.number_of_questions,
      )}/${quizDetails?.number_of_questions}`,
      subtitle: 'Quiz.bestScore',
      icon: (
        <View
          style={{ flex: 0.2, marginRight: 8, flexDirection: 'row', justifyContent: 'flex-start' }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.4267 5.57333C21.9467 6.05333 21.9067 6.8 22.3333 7.33333C24.2667 9.76 25.28 13.08 24.3733 16.56C23.32 20.6267 20.1333 23.6667 16.0933 24.48C9.29332 25.8267 3.33332 20.5733 3.33332 14C3.33332 8.56 7.39999 4.08 12.6667 3.42667V6.12C8.49332 6.82667 5.42665 10.7333 6.09332 15.2267C6.61332 18.7067 9.50666 21.5067 13 21.9333C17.8533 22.5333 22 18.7467 22 14C22 12.3333 21.4933 10.8 20.6267 9.52C20.1733 8.85333 19.2 8.81333 18.6267 9.37333L18.6133 9.38667C18.16 9.84 18.12 10.5467 18.4667 11.08C19.2667 12.3067 19.5867 13.88 19.12 15.5067C18.6 17.3333 17.0667 18.7867 15.2 19.2C11.7333 19.96 8.66665 17.3467 8.66665 14C8.66665 11.52 10.3733 9.45333 12.6667 8.85333V11.7067C11.8667 12.1733 11.3333 13.0133 11.3333 14C11.3333 15.4667 12.5333 16.6667 14 16.6667C15.4667 16.6667 16.6667 15.4667 16.6667 14C16.6667 13.0133 16.1333 12.16 15.3333 11.7067V1.61333C15.3333 1.09333 14.9067 0.666667 14.3867 0.666667C7.23999 0.4 1.07999 6.08 0.693321 13.2C0.213321 21.9333 7.90666 28.96 16.7067 27.0667C21.88 25.96 25.88 22 27.04 16.84C27.9867 12.6267 26.7733 8.62667 24.4133 5.68C23.92 5.05333 22.9867 5.01333 22.4267 5.57333Z"
              fill="#1FE08F"
            />
          </svg>
        </View>
      ),
      options: {},
      showTooltip: false,
    })
  }

  statCards = [
    ...statCards,
    {
      title:
        quizDetails?.max_attempts !== 999999 && enterpriseTier
          ? `${quizDetails?.completed_attempts}/${quizDetails?.max_attempts}`
          : String(quizDetails?.completed_attempts),
      subtitle: 'Quiz.timesTaken',
      icon: (
        <View
          style={{ flex: 0.2, marginRight: 8, flexDirection: 'row', justifyContent: 'flex-start' }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.1333 23.8267L19.4667 26.1467C18.1867 26.7333 16.7867 27.12 15.3333 27.2667V24.5733C16.32 24.4533 17.2533 24.2 18.1333 23.8267ZM3.42667 15.3333H0.733337C0.880004 16.8 1.26667 18.1867 1.85334 19.4667L4.17334 18.1333C3.8 17.2533 3.54667 16.32 3.42667 15.3333ZM18.1333 4.17333L19.4667 1.85333C18.1867 1.26667 16.8 0.88 15.3333 0.733334V3.42667C16.32 3.54667 17.2533 3.8 18.1333 4.17333ZM24.5733 12.6667H27.2667C27.12 11.2 26.7333 9.81333 26.1467 8.53333L23.8267 9.86667C24.2 10.7467 24.4533 11.68 24.5733 12.6667ZM9.86667 23.8267L8.53334 26.1467C9.81334 26.7333 11.2133 27.12 12.6667 27.2667V24.5733C11.68 24.4533 10.7467 24.2 9.86667 23.8267ZM12.6667 3.42667V0.733334C11.2 0.88 9.81334 1.26667 8.53334 1.85333L9.86667 4.17333C10.7467 3.8 11.68 3.54667 12.6667 3.42667ZM22.48 7.56L24.8 6.21333C23.96 5.05333 22.9333 4.02667 21.7733 3.18667L20.4267 5.50667C21.2133 6.10667 21.8933 6.78667 22.48 7.56ZM4.17334 9.86667L1.85334 8.53333C1.26667 9.81333 0.880004 11.2 0.733337 12.6667H3.42667C3.54667 11.68 3.8 10.7467 4.17334 9.86667ZM24.5733 15.3333C24.4533 16.32 24.2 17.2533 23.8267 18.1333L26.1467 19.4667C26.7333 18.1867 27.12 16.7867 27.2667 15.3333H24.5733ZM20.44 22.48L21.7867 24.8C22.9467 23.96 23.9733 22.9333 24.8133 21.7733L22.4933 20.4267C21.8933 21.2133 21.2133 21.8933 20.44 22.48ZM7.56 5.52L6.22667 3.18667C5.05334 4.04 4.04 5.05333 3.2 6.22667L5.52 7.57333C6.10667 6.78667 6.78667 6.10667 7.56 5.52ZM5.52 20.44L3.2 21.7733C4.04 22.9333 5.06667 23.96 6.22667 24.8L7.57334 22.48C6.78667 21.8933 6.10667 21.2133 5.52 20.44ZM14 7.33333C13.2667 7.33333 12.6667 7.93333 12.6667 8.66667V13.4533C12.6667 14.16 12.9467 14.84 13.4533 15.3333L17.4533 19.3333C17.9733 19.8533 18.8133 19.8533 19.3333 19.3333C19.8533 18.8133 19.8533 17.9733 19.3333 17.4533L15.3333 13.4533V8.66667C15.3333 7.93333 14.7333 7.33333 14 7.33333Z"
              fill="#FF7324"
            />
          </svg>
        </View>
      ),
      options: {},
      showTooltip: false,
    },
  ]

  if (quizDetails?.completed_attempts && quizDetails?.completed_attempts > 0) {
    statCards.push({
      title: `${Number(Math.round(Number(quizDetails?.current_recall) * 100).toFixed(0))}%`,
      subtitle: 'Profile.recall',
      onPress: () => setShowTooltip(true),
      showTooltip: true,
      icon: (
        <View
          style={{ flex: 0.2, marginRight: 8, flexDirection: 'row', justifyContent: 'flex-start' }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.34502 22.8711C2.56844 23.2927 1.58897 23.0074 1.24696 22.1926C0.382509 20.1331 -0.0435171 17.9097 0.00350976 15.6649C0.0617392 12.8853 0.843234 10.1689 2.27098 7.78332C3.69872 5.39774 5.72343 3.42537 8.14556 2.06057C10.5677 0.695771 13.3036 -0.0143403 16.0838 0.000219408C18.8639 0.0147791 21.5923 0.753507 24 2.1436C26.4077 3.53369 28.4117 5.52717 29.8143 7.92756C31.217 10.328 31.97 13.0524 31.9991 15.8325C32.0226 18.0776 31.5733 20.2965 30.6874 22.3468C30.3368 23.1579 29.3544 23.433 28.5823 23.0032C27.8102 22.5735 27.543 21.6018 27.8731 20.7821C28.5003 19.2251 28.817 17.5548 28.7993 15.866C28.776 13.6419 28.1736 11.4624 27.0515 9.54205C25.9293 7.62173 24.3262 6.02695 22.4 4.91488C20.4738 3.80281 18.2911 3.21182 16.067 3.20018C13.8429 3.18853 11.6542 3.75662 9.71645 4.84845C7.77874 5.94029 6.15897 7.51819 5.01678 9.42665C3.87459 11.3351 3.24939 13.5083 3.20281 15.7319C3.16743 17.4205 3.46665 19.094 4.07741 20.6575C4.39894 21.4806 4.12159 22.4494 3.34502 22.8711Z"
              fill="#E4E4E4"
            />
            <path
              d="M3.34502 22.8711C2.56844 23.2927 1.58894 23.0073 1.24691 22.1926C-0.175611 18.8039 -0.394137 15.0107 0.658895 11.4557C1.86411 7.38701 4.63626 3.96371 8.36549 1.93891C12.0947 -0.0858882 16.4756 -0.546323 20.5443 0.658894C24.0992 1.71192 27.1615 3.96115 29.2287 6.99979C29.7257 7.73041 29.4316 8.7073 28.655 9.12894V9.12894C27.8784 9.55058 26.9148 9.25548 26.3996 8.53752C24.7507 6.2395 22.3761 4.53895 19.6354 3.72711C16.3805 2.76294 12.8758 3.13129 9.89239 4.75113C6.90901 6.37097 4.69129 9.1096 3.72712 12.3646C2.91528 15.1053 3.04816 18.023 4.07737 20.6575C4.39891 21.4805 4.12159 22.4494 3.34502 22.8711V22.8711Z"
              fill="url(#paint0_linear_1045_13288)"
            />
            <path
              d="M13.0363 14.7056L13.0362 14.7057L9.18256 18.2606C9.18254 18.2606 9.18251 18.2607 9.18248 18.2607C9.18247 18.2607 9.18245 18.2607 9.18243 18.2607C8.83799 18.5781 8.83803 19.0992 9.18256 19.4165C9.5168 19.7243 10.0503 19.7243 10.3846 19.4165L10.3847 19.4163L13.6342 16.418L15.599 18.2275C15.9332 18.5353 16.4667 18.5353 16.8009 18.2276C16.8009 18.2276 16.8009 18.2276 16.801 18.2276C16.801 18.2276 16.801 18.2275 16.801 18.2275L20.2431 15.0632L21.02 15.7786C21.1891 15.9344 21.4158 15.9522 21.5931 15.8852C21.7709 15.8181 21.9329 15.6505 21.9329 15.4107V12.8708C21.942 12.5619 21.6788 12.3527 21.3994 12.3527H18.644C18.4132 12.3527 18.2279 12.4831 18.146 12.6638C18.0634 12.8463 18.0906 13.0749 18.2645 13.2351L19.0147 13.926L16.2033 16.5152L14.2383 14.7056C13.9041 14.3978 13.3705 14.3978 13.0363 14.7056Z"
              fill="url(#paint1_linear_1045_13288)"
              stroke="url(#paint2_linear_1045_13288)"
              strokeWidth="0.4375"
            />
            <defs>
              <linearGradient
                id="paint0_linear_1045_13288"
                x1="34.4"
                y1="26.1104"
                x2="-3.2"
                y2="26.1104"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#6BD5BF" />
                <stop offset="0.159669" stopColor="#32D172" />
                <stop offset="0.268196" stopColor="#8ACD41" />
                <stop offset="0.658066" stopColor="#F3AF3D" />
                <stop offset="0.804268" stopColor="#FF7A00" />
                <stop offset="1" stopColor="#FF2E00" />
              </linearGradient>
              <linearGradient
                id="paint1_linear_1045_13288"
                x1="19.7657"
                y1="16.2857"
                x2="10.9657"
                y2="16.2857"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#52D060" />
                <stop offset="0.395833" stopColor="#EDB13E" />
                <stop offset="1" stopColor="#FF7501" />
              </linearGradient>
              <linearGradient
                id="paint2_linear_1045_13288"
                x1="19.7657"
                y1="16.2857"
                x2="10.9657"
                y2="16.2857"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#52D060" />
                <stop offset="0.395833" stopColor="#EDB13E" />
                <stop offset="1" stopColor="#FF7501" />
              </linearGradient>
            </defs>
          </svg>
        </View>
      ),
      options: {},
    })
  }

  const renderTimer = () => {
    if (quizDetails?.completed_attempts === 0) {
      if (quizDetails?.due_date) {
        return (
          <>
            {contestActive && (
              <TouchableOpacity
                onPress={() => h.push(`/play/contests/${contestActive}`)}
                style={{ ...TROPHY, marginRight: 16 }}
              >
                <View style={{ top: 9, left: 0.4 }}>
                  <SvgIcon size={15} src={iconTrophySmall} color={color.white} />
                </View>
              </TouchableOpacity>
            )}
            {!userStore.isSubscriptionWorkspace && (
              <CountdownTimer locale={user?.language ?? 'en'} date={quizDetails.due_date} />
            )}
          </>
        )
      }
      if (quizDetails?.end_date && !quizDetails?.due_date) {
        return (
          <>
            {contestActive && (
              <TouchableOpacity
                onPress={() => h.push(`/play/contests/${contestActive}`)}
                style={{
                  borderRadius: 100,
                  width: 32,
                  height: 32,
                  marginRight: 16,
                  alignItems: 'center',
                  backgroundColor: color.black,
                }}
              >
                <View style={{ top: 9, left: 0.4 }}>
                  <SvgIcon size={15} src={iconTrophySmall} color={color.white} />
                </View>
              </TouchableOpacity>
            )}
            {!userStore.isSubscriptionWorkspace && (
              <CountdownTimer locale={user?.language ?? 'en'} date={quizDetails.end_date} />
            )}
          </>
        )
      }
    }
    return contestActive ? (
      <TouchableOpacity onPress={() => h.push(`/play/contests/${contestActive}`)} style={TROPHY}>
        <View style={{ top: 9, left: 0.3 }}>
          <SvgIcon size={15} src={iconTrophySmall} color={color.white} />
        </View>
      </TouchableOpacity>
    ) : null
  }

  const requisite = quizDetails?.resources?.filter((resource) => resource.is_required)[0]
  const isMobile = !useMediaQuery('(min-width:800px)')

  return (
    <>
      <View style={CONTAINER}>
        <div style={HEADER_STICKY_CONTAINER}>
          <div style={HEADER_FLEX_CONTAINER}>
            <div style={HEADER}>
              <Header
                leftIcon={iconArrowBack}
                onLeftPress={h.goBack}
                center
                headerTx="Quiz.header"
              />
            </div>
          </div>
        </div>
        {isLoading ? (
          <ActivityIndicator color="#999" size="small" style={LOADER} />
        ) : (
          <>
            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingBottom: isMobile ? 200 : 20 }}
            >
              {quizDetails?.photo_url ? (
                <div
                  style={{
                    marginBottom: 32,
                    position: 'relative',
                    height: 141,
                    borderRadius: 16,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={quizDetails?.photo_url}
                    style={{ height: 141, width: '100%', objectFit: 'cover', position: 'absolute' }}
                  />
                </div>
              ) : null}
              <Text
                align="center"
                text={quizDetails?.title ?? ''}
                variant="headline"
                style={{ marginBottom: 20 }}
              />
              {(contestActive || quizDetails?.end_date) && enterpriseTier && (
                <View style={DUE_CONTAINER}>
                  {params.path_id ? (
                    <TouchableOpacity
                      onPress={() =>
                        h.push(
                          `/library/path/?assignment_id=${params.assignment_id}&path_id=${params.path_id}`,
                        )
                      }
                      style={{
                        borderRadius: 100,
                        width: 32,
                        height: 32,
                        borderWidth: 2,
                        borderColor: color.tint70,
                        marginRight:
                          selectedTopic?.due_date &&
                          selectedTopic?.quizzes_completed !== selectedTopic?.quizzes.length &&
                          contestActive
                            ? 16
                            : 0,
                        alignItems: 'center',
                        backgroundColor: color.shade70,
                      }}
                    >
                      <View style={{ top: 6.5, left: 0.35 }}>
                        <SvgIcon size={15} src={iconPath} color={color.white} />
                      </View>
                    </TouchableOpacity>
                  ) : null}
                  {renderTimer()}
                </View>
              )}
              <Text
                variant="body1Medium"
                text={quizDetails?.description}
                style={{ textAlign: 'center', marginBottom: 20 }}
              />
              <QuizHeader
                disabled={
                  quizDetails?.locked_until ||
                  (quizDetails?.completed_attempts ?? 0) >= (quizDetails?.max_attempts ?? 0)
                }
                onPress={() => {
                  if (requisite && !viewedReq && quizDetails?.quiz_status !== 'In Progress') {
                    confirmRequisite()
                  } else {
                    start()
                  }
                }}
                enterpriseTier={enterpriseTier}
                style={{ marginHorizontal: 0 }}
                inProgress={quizDetails?.quiz_status === 'In Progress'}
                medal={quizDetails?.medal_level}
                maxAttempts={quizDetails?.max_attempts}
                completedAttempts={quizDetails?.completed_attempts ?? 0}
                recall={
                  !enterpriseTier
                    ? (quizDetails?.best_score ?? 0) / 100
                    : quizDetails?.current_recall ?? 0
                }
                questionCount={quizDetails?.number_of_questions ?? 0}
              />
              <FlatList
                numColumns={2}
                data={statCards}
                style={{ paddingHorizontal: 4, marginBottom: 12, paddingTop: 20 }}
                renderItem={({ item, index }: any) => {
                  return <QuizStatsPanel total={statCards.length} item={item} index={index} />
                }}
              />
              {quizDetails?.completed_attempts === 0 && quizDetails?.resources.length === 0 && (
                <QuizNull style={{ alignSelf: 'center', marginBottom: 40 }} />
              )}
              <View style={{ marginBottom: 8, paddingLeft: 4, paddingRight: 4 }}>
                {quizDetails?.resources ? renderRequiredResource() : null}
                {quizDetails?.resources &&
                  quizDetails?.resources.length > 0 &&
                  quizDetails.resources
                    .filter((resource) => !resource?.is_required)
                    .map((resource) => {
                      return (
                        <ResourceRow
                          disabled={Boolean(
                            selectedTopic?.pre_test && !selectedTopic?.pre_test.completed,
                          )}
                          onPress={() => viewResource(resource)}
                          resource={resource}
                          style={{ marginBottom: 16 }}
                        />
                      )
                    })}
              </View>
              {renderHistory()}
            </ScrollView>
            <QuizFooter
              requisite={requisite}
              showResource={confirmRequisite}
              start={start}
              quizDetails={quizDetails}
              loading={quizStore.isLoading}
              viewedReq={viewedReq}
              limitStatus={limitStatus}
            />
          </>
        )}
      </View>
      <Dialog
        maxWidth="sm"
        align="left"
        onClose={() => {
          setViewingTextSource(null)
          onFocus()
        }}
        title="Preview"
        open={Boolean(viewingTextSource)}
      >
        <Box flexDirection="column" display="flex" p={2} sx={{ whiteSpace: 'pre-wrap' }}>
          <Text style={{ marginBottom: 16 }} variant="headline" text={viewingTextSource?.title} />
          {viewingTextSource?.raw_text}
        </Box>
      </Dialog>
    </>
  )
})

interface QuizFooterProps {
  loading?: boolean
  quizDetails: Instance<typeof PreQuiz> | undefined
  start?: () => void
  showResource?: () => void
  requisite?: Instance<typeof QuizResource>
  viewedReq?: boolean
  limitStatus?: Instance<typeof GetLimitStatus>
}
const QuizFooter = observer((props: QuizFooterProps) => {
  const { viewedReq, loading, requisite, showResource, quizDetails, start, limitStatus } = props
  const renderLockedTime = (lockDate: string | null | undefined) => {
    if (lockDate) {
      const lockedUntil = DateTime.fromJSDate(new Date(lockDate))
      return lockedUntil.toRelative()
    }
    return ''
  }

  const isMobile = !useMediaQuery('(min-width:800px)')

  if (limitStatus?.respondent_limit_reached) {
    return (
      <div
        style={{
          width: '100%',
          flexDirection: 'row',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            ...START_CONTAINER,
            padding: 20,
            position: isMobile ? 'fixed' : 'relative',
            borderTop: isMobile ? '1px solid #EFF0F6' : 'none',
            bottom: isMobile ? 70 : 0,
            maxWidth: isMobile ? '100%' : 450,
            width: '100%',
          }}
        >
          <Text
            text={`This Trivie workspace has reached its quiz limit for the month.`}
            variant="titleEmphasized"
            style={{
              marginHorizontal: 20,
              color: color.black,
              marginBottom: 24,
              textAlign: 'center',
            }}
          />
          <Button
            accessibilityRole="button"
            style={{
              marginHorizontal: 20,
              ...START_BUTTON,
            }}
            textStyle={{
              ...START_BUTTON_TEXT,
            }}
            tx="Game.requestAccess"
            onPress={() => {
              const adminEmailAddress = limitStatus?.owner_email
              const subjectText = 'Requesting access for Trivie'
              const bodyText = `${limitStatus?.owner_display_name},
              
              I tried to take ${quizDetails?.title} but wasn’t able to because we’ve reached our quiz limit for the month.
              
              Please log in and upgrade our plan so I can start learning!`

              window.open(`mailto:${adminEmailAddress}?subject=${subjectText}&body=${bodyText}`)
            }}
          />
        </div>
      </div>
    )
  }

  return !loading ? (
    <div
      style={{
        width: '100%',
        flexDirection: 'row',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          ...START_CONTAINER,
          padding: 20,
          position: isMobile ? 'fixed' : 'relative',
          borderTop: isMobile ? '1px solid #EFF0F6' : 'none',
          bottom: isMobile ? 70 : 0,
          maxWidth: isMobile ? '100%' : 450,
          width: '100%',
        }}
      >
        <div style={{ flexDirection: 'column', display: 'flex' }}>
          {requisite && (
            <Button
              accessibilityRole="button"
              accessibilityLabel="View required resource"
              variant="secondary"
              style={{ marginBottom: 18 }}
              onPress={showResource}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', alignSelf: 'center' }}>
                <Eye style={{ marginRight: 7 }} />
                <Text
                  style={{ color: color.white }}
                  tx="Resources.viewRequired"
                  variant="labelEmphasized"
                />
              </View>
            </Button>
          )}
          <Button
            accessibilityRole="button"
            accessibilityLabel={
              quizDetails?.locked_until
                ? 'Unlock game'
                : quizDetails?.quiz_status === 'In Progress'
                  ? 'Continue quiz'
                  : quizDetails?.max_attempts !== 0 &&
                      (quizDetails?.completed_attempts ?? 0) >= (quizDetails?.max_attempts ?? 0)
                    ? 'You reached max limit for this quiz'
                    : 'Start quiz'
            }
            disabled={Boolean(
              quizDetails?.locked_until ||
                (quizDetails?.max_attempts !== 0 &&
                  (quizDetails?.completed_attempts ?? 0) >= (quizDetails?.max_attempts ?? 0)) ||
                (!viewedReq && requisite && quizDetails?.quiz_status !== 'In Progress'),
            )}
            txOptions={{ unlock: renderLockedTime(quizDetails?.locked_until) }}
            tx={
              quizDetails?.locked_until
                ? 'Game.unlocksIn'
                : quizDetails?.quiz_status === 'In Progress'
                  ? 'Game.continueQuiz'
                  : quizDetails?.max_attempts !== 0 &&
                      (quizDetails?.completed_attempts ?? 0) >= (quizDetails?.max_attempts ?? 0)
                    ? 'Game.maxReached'
                    : 'Game.startQuiz'
            }
            onPress={start}
          />
        </div>
      </div>
    </div>
  ) : null
})

/**
 * Styling
 */
const CONTAINER: ViewStyle = {
  height: '100%',
  backgroundColor: color.white,
}
const START_CONTAINER: React.CSSProperties = {
  backgroundColor: color.white,
  width: '100%',
}
const START_BUTTON: ViewStyle = {
  backgroundColor: color.blue,
  height: 52,
  paddingVertical: 15,
  borderRadius: 16,
  width: 335,
  alignSelf: 'center',
}
const START_BUTTON_TEXT: TextStyle = {
  fontSize: 17,
  lineHeight: 22,
  fontWeight: '600',
}
const LOADER: ViewStyle = { flex: 1, alignSelf: 'center', bottom: 60 }
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER: React.CSSProperties = { width: '100%' }
const DUE_CONTAINER: ViewStyle = {
  padding: 3,
  borderRadius: 16,
  alignSelf: 'center',
  marginBottom: 13,
  flexDirection: 'row',
  alignItems: 'center',
}

const TROPHY: ViewStyle = {
  borderRadius: 100,
  width: 32,
  height: 32,
  alignItems: 'center',
  backgroundColor: color.black,
}
